<script>
	import { getContext } from 'svelte'
	import DraggableComponent from '@Components/DraggableComponent.svelte'
	import { deconstructHoleId } from '@Utils/DataController.js'

	import { IconDropDownMenu, HoleIconDropDownMenu, IconCollection } from './Toolbar/IconDropDownMenu.js'
	import { TreeSelectList, BushDropDownMenu } from './Toolbar/TreeDropDownMenu.js'

	let dataSources = {
		misc: IconDropDownMenu,
		holeSpecific: HoleIconDropDownMenu,
		iconCollection: IconCollection,
		tees: [],
		trees: TreeSelectList.map((tree) => {
			return {
				title: tree.title,
				value: tree.props.textureRef
			}
		}),
		bushes: BushDropDownMenu.map((bush) => {
			return {
				title: bush.title,
				value: bush.props.textureRef
			}
		})
	}
	let Pack2 = {
		tree1SvgUrl: '/images/Tree1.svg',
		tree2SvgUrl: '/images/Tree2.svg',
		tree3SvgUrl: '/images/Tree3.svg',
		tree4SvgUrl: '/images/Tree4.svg',
		tree5SvgUrl: '/images/Tree5.svg',
		tree6SvgUrl: '/images/Tree6.svg',
		tree7SvgUrl: '/images/Tree7.svg'
	}
	const { getEditorCanvas, getController } = getContext('editor')
	const editorCanvas = getEditorCanvas()
	const controller = getController()
	let showAdvanced = $state(import.meta.env.VITE_ADVANCED_TOOLING_ENABLED === 'true' && localStorage.getItem('showAdvancedOptions') === 'true')
	let selectedOptions = $state(null);
	let currentFrame = $state(null);
	let currentHoleId = $state(null);
	let currentCourseId = $state(null);
	let title = $state('Options');
	let currentElementFeature = $state(null);
	let values = $state(null);
	let isHidden = $state(true);
	let isDarkMode = $state(window.isDarkMode);
	let currentTool = $state(null);
	let simplifyEnabled = $state(false);
	let editEnabled = $state(false);
	let playlineOptions = $state(null);
	let courseFrameOptions = $state(null);
	window.addEventListener('colorSchemeChanged', (e) => {
		isDarkMode = e.isDarkMode
	})
	function reset() {
		selectedOptions = null
		playlineOptions = null
		currentFrame = null
		currentElementFeature = null
		values = null
		isHidden = true
		simplifyEnabled = false
		editEnabled = false
		editorCanvas.deselectFeature()
	}
	function hidden(field) {
		if (!selectedOptions) {
			return false
		}
		if (field.hidden) {
			return true
		}
		if (!activated(field)) {
			return true
		}
		if (!field.hiddenFor) {
			return false
		}

		let isObject = typeof field.hiddenFor === 'object'
		if (isObject) {
			if (field.hiddenFor.name === 'elementName') {
				return field.hiddenFor.values.includes(currentElementFeature.getElement().name)
			}
			for (let option of selectedOptions) {
				let f = option.fields.find((f) => f.name === field.hiddenFor.name)
				if (f && f.value) {
					if (field.hiddenFor.values) {
						return field.hiddenFor.values.includes(f.value)
					} else if (field.hiddenFor.value) {
						return field.hiddenFor.value === f.value
					} else if (field.hiddenFor.excludeValues) {
						return !field.hiddenFor.excludeValues.includes(f.value)
					} else if (field.hiddenFor.excludeValue) {
						return !field.hiddenFor.excludeValue === f.value
					}
					return false
				}
			}
		} else {
			for (let option of selectedOptions) {
				let f = option.fields.find((f) => f.name === field.hiddenFor)
				if (f && f.value) {
					return field.hiddenFor.value === f.value
				}
			}
		}
		return false
	}
	function activated(field) {
		if (!selectedOptions) {
			return false
		}
		if (!field.activatedBy) {
			return true
		}
		let isObject = typeof field.activatedBy === 'object'
		if (isObject) {
			for (let option of selectedOptions) {
				let f = option.fields.find((f) => f.name === field.activatedBy.name)
				if (f && f.value) {
					return field.activatedBy.values.includes(f.value) && activated(f) && !hidden(f)
				}
			}
		}
		for (let option of selectedOptions) {
			let f = option.fields.find((f) => f.name === field.activatedBy)
			if (f && f.value && activated(f)) {
				if (f.unhideRelatedOptions && hidden(f)) {
					return true
				}
				return !hidden(f)
			}
		}
		return false
	}
	function loadOptionsForHoleFrame(feature) {
		let options = [
			{
				fields: [
					{
						name: 'iconScale',
						title: 'Icon scale',
						type: 'range',
						default: 1,
						max: 3,
						min: 0.1,
						step: 0.01,
						value: feature.get('iconScale')
					}
				]
			}
		]
		title = 'Frame options'
		values = {
			iconScale: feature.get('iconScale') ?? 1
		}
		selectedOptions = options
	}
	function loadOptionsForCourseFrame(feature) {
		const loops = controller.getLoops()
		let allCourseFrames = editorCanvas.getCourseFrameFeatures()
		let disabled = {}
		for (let l of loops) {
			for (let frame of allCourseFrames) {
				if (frame.get('courseId') === feature.get('courseId')) {
					continue
				}
				let frameLoops = frame.get('loops') ?? []
				if (frameLoops.includes(l.id)) {
					disabled[l.id] = true
				}
			}
		}
		courseFrameOptions = [
			{
				fields: [
					{
						name: 'displayName',
						title: 'Display name',
						type: 'text',
						value: feature.get('displayName')
					},
					{
						name: 'iconScale',
						title: 'Icon scale',
						type: 'range',
						default: 1,
						max: 3,
						min: 0.1,
						step: 0.01,
						value: feature.get('iconScale')
					},
					{
						name: 'loops',
						title: 'Connect courses',
						type: 'multiselect',
						options: loops.map((loop) => {
							return {
								title: loop.name,
								value: loop.id,
								disabled: disabled[loop.id]
							}
						}),
						value: feature.get('courseId')
					}
				]
			}
		]
		title = 'Course options'
		values = {
			loops: feature.get('loops') ?? [],
			iconScale: feature.get('iconScale') ?? 1,
			displayName: feature.get('displayName')
		}
		selectedOptions = courseFrameOptions
	}
	function loadOptions(elementFeature) {
		let subscription = controller.getSubscription()
		if (elementFeature === undefined) {
			elementFeature = currentElementFeature
		}
		if (!elementFeature) {
			reset()
			return
		}
		simplifyEnabled = elementFeature.getType() === 'Polygon' || elementFeature.getType() === 'LineString'
		editEnabled = elementFeature.getType() === 'Polygon'
		showAdvanced = import.meta.env.VITE_ADVANCED_TOOLING_ENABLED === 'true' && localStorage.getItem('showAdvancedOptions') === 'true'
		let opt = elementFeature.getValues(showAdvanced, subscription)
		if (opt) {
			title = elementFeature.getElement().title
			selectedOptions = opt
			values = {}

			for (let option of selectedOptions) {
				for (let field of option.fields) {
					if (Array.isArray(field.dataSource)) {
						for (let source of field.dataSource) {
							if (source.elementName && source.elementName !== elementFeature.getElement().name && dataSources[source.source]) {
								continue
							}
							for (let obj of dataSources[source.source]) {
								field.options.push({
									title: obj.title ?? obj.tooltip,
									value: obj.value ?? obj.name,
									props: obj.props
								})
							}
						}
					} else if (field.dataSource && dataSources[field.dataSource]) {
						field.options = dataSources[field.dataSource]
					} else if (field.options) {
						let arr = []
						for (let option of field.options) {
							if (option.hiddenFor) {
								if (option.hiddenFor.name === 'elementName' && option.hiddenFor.values.includes(elementFeature.getElement().name)) {
									continue
								}
							}
							arr.push(option)
						}
						field.options = arr
					}
					if (field.name === 'iconScale') {
						if (currentFrame) {
							values[field.name] = currentFrame.get(field.name) ?? 1
						} else if (currentHoleId) {
							values[field.name] = editorCanvas.getFrameFeature(currentHoleId)?.get(field.name) ?? 1
						} else if (currentCourseId) {
							values[field.name] = editorCanvas.getCourseFrameFeature(currentCourseId)?.get(field.name) ?? 1
						} else {
							field.hidden = true
						}
					} else {
						values[field.name] = field.value
					}
				}
			}
			isHidden = false
		}
	}
	function togglePlayline() {
		if (currentTool !== 'playline' || !currentHoleId) {
			return
		}
		let holeProps = deconstructHoleId(currentHoleId)
		let playline = JSON.parse(editorCanvas.getPlayline(currentHoleId))
		let tees = controller.data.holeTees[holeProps.holeId]
		isHidden = false
		playlineOptions = [
			{
				name: 'greenPoint',
				title: 'Green point',
				type: 'select',
				options: [
					{ title: 'Back', value: 'greenback' },
					{ title: 'Center', value: 'greencenter' },
					{ title: 'Front', value: 'greenfront' }
				]
			}
		]
		playlineOptions.push({
			name: 'teePoint',
			title: 'Tee point',
			type: 'select',
			options: tees.map((tee) => {
				return {
					title: tee.teeAlias,
					value: 'tee' + tee.elementId
				}
			})
		})
		selectedOptions = [
			{
				fields: playlineOptions
			}
		]
		values = {
			greenPoint: playline.greenPoint,
			teePoint: playline.teePoint
		}
		title = 'Playline options'
	}
	editorCanvas.on('currentToolChange', function (tool, element) {
		currentTool = tool
		if (!tool) {
			reset()
		}
		togglePlayline()
	})

	editorCanvas.on('frameSelected', function (frame) {
		currentFrame = frame

		if (!frame) {
			reset()
			return
		}
		if (frame.get('holeId')) {
			title = `Hole outline (${deconstructHoleId(frame.get('holeId')).displayName})`
		} else if (frame.get('courseId')) {
			title = `Loop outline (${frame.get('displayName') ?? ''})`
		}
		isHidden = false
		if (frame.get('courseId')) {
			loadOptionsForCourseFrame(frame)
		} else if (frame.get('holeId')) {
			loadOptionsForHoleFrame(frame)
		} else {
			togglePlayline()
		}
	})

	editorCanvas.on('elementSelected', function (elementFeature) {
		if (!elementFeature) {
			reset()
			return
		}
		if (elementFeature.getValue('holeId')) {
			let holeProps = deconstructHoleId(elementFeature.getValue('holeId'))
			dataSources.tees = controller.data.holeTees[holeProps.holeId].map((tee) => {
				return {
					title: tee.teeAlias,
					value: 'tee' + tee.elementId
				}
			})
		} else {
			dataSources.tees = []
			dataSources.holes = []
			let array = []
			controller.data.holes.forEach((hole) => {
				array.push({
					title: hole.displayName,
					value: hole.holeId
				})
			})
			dataSources.holes = array
		}
		loadOptions(elementFeature)
		currentElementFeature = elementFeature
	})
	editorCanvas.on('frameFocused', function (frame) {
		if (!frame) {
			// clear and hide
			currentHoleId = null
			currentCourseId = null
			return
		}
		currentHoleId = frame.feature.get('holeId')
		currentCourseId = frame.feature.get('courseId')
	})
</script>

<DraggableComponent additionStyles={isHidden ? 'hidden' : ''} initialX="10">
	<div class="flex text-sm flex-col text-left pt-0" class:w-52={!showAdvanced} class:w-64={showAdvanced}>
		<div class="text-left w-full font-bold p-2 pt-0 select-none">{title}</div>
		{#if selectedOptions && selectedOptions.length > 0 && Object.keys(values).length > 0}
			<div class="flex flex-col gap-2 bg-neutral-200 dark:bg-bigsteel-900/20 p-2 options select-none">
				{#each selectedOptions as optionGroup}
					<!--div class="flex text-sm flex-col gap-2 optiongroup"-->
					{#each optionGroup.fields.filter((f) => !hidden(f)) as field}
						{#if field.type == 'color'}
							<label for={field.name} tooltip={field.tooltip}>{field.title}</label>
							<input
								bind:value={values[field.name]}
								name={field.name}
								type="color"
								class="color-input"
								class:dark-color-input={isDarkMode}
								oninput={(e) => {
									currentElementFeature.setOption(field.name, e.target.value)
								}}
								onchange={(e) => {
									field.value = e.target.value
									currentElementFeature.setOption(field.name, e.target.value)
								}}
							/>
						{:else if field.type == 'text'}
							<label for={field.name} tooltip={field.tooltip}>{field.title}</label>
							<input
								bind:value={values[field.name]}
								name={field.name}
								placeholder={field.title}
								type="text"
								class="text-input"
								class:dark-input={isDarkMode}
								oninput={(e) => {
									if (currentFrame) {
										currentFrame.set(field.name, e.target.value)
									} else if (currentElementFeature) {
										currentElementFeature.setOption(field.name, e.target.value)
									}
								}}
								onchange={(e) => {
									field.value = e.target.value
									if (currentFrame) {
										currentFrame.set(field.name, e.target.value)
									} else if (currentElementFeature) {
										currentElementFeature.setOption(field.name, e.target.value)
									}
								}}
							/>
						{:else if field.type == 'multiselect' && activated(field)}
							<div class="text-left w-full" tooltip={field.tooltip}>{field.title}</div>
							<div class="bg-neutral-400 dark:bg-bigsteel-800/70 p-2 rounded-[4px] dark:border-bigsteel-200/30 border-neutral-400 border multiselect">
								{#each field.options as option}
									<div class="flex flex-row gap-2">
										<input
											name={field.name + '' + option.value}
											disabled={!activated(field) || option.disabled}
											onchange={(e) => {
												let vals = [...(values[field.name] ?? field.default)]
												if (e.target.checked) {
													vals = [...values[field.name], option.value]
												} else {
													vals = vals.filter((v) => v !== option.value)
												}
												values[field.name] = vals.filter((v) => v)
												field.value = vals
												if (currentElementFeature) {
													currentElementFeature.setOption(field.name, vals)
												} else if (currentFrame) {
													currentFrame.set(field.name, vals)
												}
											}}
											title={values[field.name]}
											type="checkbox"
											checked={values[field.name].includes(option.value)}
										/>
										<label for={field.name + '' + option.value} tooltip={option.tooltip}>{option.title}</label>
									</div>
								{/each}
							</div>
						{:else if field.type == 'select' && activated(field)}
							<label tooltip={field.tooltip} for={field.name}>{field.title}</label>
							<select
								bind:value={values[field.name]}
								name={field.name}
								disabled={!activated(field)}
								onchange={(e) => {
									field.value = e.target.value
									if (currentElementFeature) {
										currentElementFeature.setOption(field.name, e.target.value)
										let option = field.options.find((option) => option.value === e.target.value)
										if (option && option.props) {
											for (let prop in option.props) {
												currentElementFeature.setOption(prop, option.props[prop])
											}
											loadOptions(currentElementFeature)
										}
									} else if (playlineOptions) {
										editorCanvas.setPlaylineOption(currentHoleId, field.name, e.target.value)
									}
								}}
							>
								{#each field.options as option}
									<option value={option.value}>{option.title}</option>
								{/each}
							</select>
						{:else if field.type == 'grid'}
							<div class="text-left w-full" tooltip={field.tooltip}>{field.title}</div>
							<div class="grid grid-cols-5 gap-2">
								{#each field.options as option}
									<button
										class="image-button"
										class:image-button-lighter={isDarkMode}
										selected={option.value == field.value}
										onclick={() => {
											values[field.name] = option.value
											field.value = option.value
											currentElementFeature.setOption(field.name, option.value)
										}}><img src={Pack2[option.value + 'SvgUrl']} alt={option.title} /></button
									>
								{/each}
							</div>
						{:else if field.type == 'range'}
							<label for={field.name} tooltip={field.tooltip}>{field.title}</label>
							<div class="flex justify-between">
								<input
									name={field.name}
									oninput={(e) => {
										const newValue = Number(e.target.value)
										if (currentElementFeature && field.name !== 'iconScale') {
											currentElementFeature.setOption(field.name, Number(e.target.value))
										} else {
											editorCanvas.setIconScale(newValue)
										}
									}}
									onchange={(e) => {
										field.value = e.target.value
										const newValue = Number(e.target.value)
										if (currentElementFeature && field.name !== 'iconScale') {
											currentElementFeature.setOption(field.name, newValue)
										} else {
											editorCanvas.setIconScale(newValue)
										}
									}}
									class="flex-1"
									disabled={!activated(field)}
									type="range"
									min={field.min}
									max={field.max}
									step={field.step}
									bind:value={values[field.name]}
								/>
								<div class:hidden={!showAdvanced} class="w-10 text-right">{values[field.name]}</div>
							</div>
						{:else if field.type == 'checkbox'}
							<div class="flex flex-row gap-2">
								<input
									name={field.name}
									disabled={!activated(field)}
									onchange={(e) => {
										values[field.name] = e.target.checked
										field.value = e.target.checked
										currentElementFeature.setOption(field.name, e.target.checked)
										if (field.name === 'isHoleSpecific' && e.target.checked && currentHoleId) {
											if ((values['targetHoles'] ?? []).length < 1) {
												values['targetHoles'] = [currentHoleId]
												currentElementFeature.setOption('targetHoles', [currentHoleId])
											}
										}
									}}
									title={values[field.name]}
									type="checkbox"
									bind:checked={values[field.name]}
								/>
								<label for={field.name} tooltip={field.tooltip}>{field.title}</label>
							</div>
						{/if}
					{/each}
					<!-- </div> -->
				{/each}
			</div>
		{/if}
		{#if !currentFrame && !playlineOptions}
			<div class="flex gap-2 p-2" class:pb-0={currentElementFeature !== null}>
				{#if simplifyEnabled}
					<button
						name="simplify"
						class="btn btn-sm flex-1"
						tooltip="Simplify a shape to reduce the number of points while keeping the shape relatively intact."
						class:btn-solid-blue={!isDarkMode}
						class:btn-solid-white={isDarkMode}
						onclick={() => {
							currentElementFeature.simplify()
						}}>Simplify</button
					>
				{/if}
				<button
					name="reset"
					class="btn btn-sm flex-1"
					tooltip="Reset settings to default values. Will not impact the underlying shape of the element."
					class:btn-solid-blue={!isDarkMode}
					class:btn-solid-white={isDarkMode}
					onclick={() => {
						if (currentElementFeature) {
							let addDefaultValuesToFeature = currentElementFeature.getElement().name === 'holeicon'
							currentElementFeature.resetToDefaults(null, false, 2, addDefaultValuesToFeature)
							loadOptions(currentElementFeature)
						} else if (playlineOptions && currentHoleId) {
							editorCanvas.resetPlaylineOptions(currentHoleId)
						}
					}}>Reset</button
				>
				{#if editEnabled}
					<button
						name="edit"
						class="btn btn-sm flex-1"
						tooltip="Open and edit element, escape to cancel."
						class:btn-solid-blue={!isDarkMode}
						class:btn-solid-white={isDarkMode}
						onclick={() => {
							editorCanvas.editSelectedFeature()
						}}>Edit</button
					>
				{/if}
				<!-- {#if currentElementFeature && currentElementFeature.getType() === 'LineString'}
					<button
						name="delete"
						class="btn btn-sm flex-1"
						tooltip="Close the cap between the first and last point of a shape."
						class:btn-solid-blue={!isDarkMode}
						class:btn-solid-white={isDarkMode}
						disabled={currentElementFeature.isClosed()}
						onclick={() => {
							currentElementFeature.close();
						}}>Close</button
					>
				{/if} -->
			</div>
		{/if}
		{#if currentFrame || currentElementFeature}
			<div class="flex gap-2 p-2">
				{#if currentFrame && !currentFrame.get('focused')}
					<button
						name="edit"
						tooltip="Press to add tee, green, and focus"
						class="btn btn-sm flex-1"
						class:btn-solid-blue={!isDarkMode}
						class:btn-solid-white={isDarkMode}
						onclick={() => {
							editorCanvas.focus(currentFrame)
						}}>{currentFrame.get('holeId') ? 'Edit hole' : 'Edit guide'}</button
					>
				{/if}
				<button
					name="delete"
					class="btn btn-sm btn-solid-red flex-1"
					onclick={() => {
						editorCanvas.deleteSelected(true)
						reset()
					}}>Delete</button
				>
			</div>
		{/if}
	</div></DraggableComponent
>

<style lang="postcss">
	.options {
		max-height: calc(100vh - 200px);
		overflow-y: auto;
	}
	.image-button:not([selected='true']) img {
		filter: light-dark(grayscale(50%) opacity(0.7), grayscale(60%) opacity(0.5));
	}
	.multiselect {
		max-height: 200px;
		min-height: 50px;
		overflow-y: auto;
	}
</style>
